import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type { TalentGeoCommuneRow, TalentGeoOfferRow } from "@/api/types";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t } from "@/i18n";
import { formatInt, formatPercent } from "@/lib/format";
import { includeTestSessions, selectedTenant, talentGeoWindow } from "@/state/filters";

// Talent geography from the per-turn Lehrstellensuche capture: demand by home
// commune, supply gaps (ÖV distance to the nearest shown offer), and where
// the shown offers sit. min_n suppresses commune rows below the threshold —
// any EXTERNALLY shared report must use min_n ≥ 10 (same k-doctrine as the
// weekly aggregates); 1 is internal admin-only visibility.
const MIN_N_CHOICES = [1, 5, 10, 25];

export function TalentGeoView() {
  const [minN, setMinN] = useState(1);
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.talentGeo(
        {
          window: talentGeoWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
          minN,
        },
        s,
      ),
    [talentGeoWindow.value, includeTestSessions.value, selectedTenant.value, minN],
  );

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px; flex-wrap: wrap;">
        <label class="toggle">
          <span>
            {t("talentgeo.minN")} <InfoDot k="talentgeo.minN" />
          </span>
          <select
            value={minN}
            onInput={(e) => setMinN(Number((e.target as HTMLSelectElement).value))}
          >
            {MIN_N_CHOICES.map((n) => (
              <option key={n} value={n}>
                {n === 10 ? t("talentgeo.minNExportable") : String(n)}
              </option>
            ))}
          </select>
        </label>
        <Staleness lastFetched={lastFetched} loading={loading} error={error} onRefresh={refetch} />
      </div>

      <section class="card">
        <h2>{t("talentgeo.overview")}</h2>
        <PanelHelp k="talentgeo.overview" />
        <div style="display: flex; gap: 32px; flex-wrap: wrap;">
          <Kpi label={t("talentgeo.searches")} value={data ? formatInt(data.listings_turns) : "—"} />
          <Kpi label={t("talentgeo.withHome")} value={data ? formatInt(data.turns_with_home) : "—"} />
          <Kpi
            label={t("talentgeo.commuteAvail")}
            value={
              data
                ? `${formatInt(data.turns_with_commute)} (${formatPercent(data.commute_coverage_rate)})`
                : "—"
            }
          />
          <Kpi label={t("talentgeo.zero")} value={data ? formatInt(data.zero_result_turns) : "—"} />
          <Kpi
            label={t("talentgeo.suppressed")}
            value={data ? formatInt(data.suppressed_communes) : "—"}
          />
        </div>
      </section>

      <section class="card">
        <h2>{t("talentgeo.demand")}</h2>
        <PanelHelp k="talentgeo.demand" />
        <DemandTable rows={data?.demand_by_commune} />
      </section>

      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
        <section class="card">
          <h2>{t("talentgeo.offers")}</h2>
          <PanelHelp k="talentgeo.offers" />
          <OfferTable rows={data?.offers_shown_by_commune} />
        </section>
        <section class="card">
          <h2>{t("talentgeo.cantons")}</h2>
          <PanelHelp k="talentgeo.cantons" />
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
            <DistTable
              title={t("talentgeo.demandByCanton")}
              data={data?.demand_by_canton}
              keyHeader={t("talentgeo.canton")}
            />
            <DistTable
              title={t("talentgeo.topProfessions")}
              data={data?.top_professions}
              keyHeader={t("quality.profession")}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div class="muted" style="font-size: 12px;">
        {label}
      </div>
      <div style="font-size: 22px; font-weight: 600;">{value}</div>
    </div>
  );
}

const fmtMin = (m: number | null) => (m === null ? "—" : t("talentgeo.minutes", { n: m }));
const communeName = (name: string, bfs: number) => name || t("talentgeo.bfs", { n: bfs });

function DemandTable({ rows }: { rows: TalentGeoCommuneRow[] | undefined }) {
  const entries = rows ?? [];
  return (
    <table class="table">
      <thead>
        <tr>
          <th>{t("talentgeo.col.commune")}</th>
          <th>{t("talentgeo.col.ct")}</th>
          <th style="text-align: right;">{t("talentgeo.col.searches")}</th>
          <th style="text-align: right;">{t("common.sessions")}</th>
          <th style="text-align: right;">{t("talentgeo.col.zero")}</th>
          <th style="text-align: right;">{t("talentgeo.col.medianNearest")}</th>
          <th style="text-align: right;">{t("talentgeo.col.noOffer30")}</th>
          <th style="text-align: right;">{t("talentgeo.col.noOffer60")}</th>
          <th>{t("talentgeo.col.topProfessions")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.length === 0 ? (
          <tr>
            <td colSpan={9} class="muted">
              {t("talentgeo.noDemand")}
            </td>
          </tr>
        ) : (
          entries.map((r) => (
            <tr key={r.bfs}>
              <td>{communeName(r.name, r.bfs)}</td>
              <td>{r.canton || "—"}</td>
              <td style="text-align: right;">{formatInt(r.searches)}</td>
              <td style="text-align: right;">{formatInt(r.sessions)}</td>
              <td style="text-align: right;">{formatInt(r.zero_result_searches)}</td>
              <td style="text-align: right;">{fmtMin(r.median_nearest_minutes)}</td>
              <td style="text-align: right;">{formatInt(r.no_offer_within_30)}</td>
              <td style="text-align: right;">{formatInt(r.no_offer_within_60)}</td>
              <td class="muted">
                {Object.entries(r.top_professions)
                  .map(([p, n]) => `${p} ×${n}`)
                  .join(", ") || "—"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function OfferTable({ rows }: { rows: TalentGeoOfferRow[] | undefined }) {
  const entries = rows ?? [];
  return (
    <table class="table">
      <thead>
        <tr>
          <th>{t("talentgeo.col.commune")}</th>
          <th>{t("talentgeo.col.ct")}</th>
          <th style="text-align: right;">{t("talentgeo.col.impressions")}</th>
          <th style="text-align: right;">{t("talentgeo.col.medianDistance")}</th>
        </tr>
      </thead>
      <tbody>
        {entries.length === 0 ? (
          <tr>
            <td colSpan={4} class="muted">
              {t("talentgeo.noOffers")}
            </td>
          </tr>
        ) : (
          entries.map((r) => (
            <tr key={r.bfs}>
              <td>{communeName(r.name, r.bfs)}</td>
              <td>{r.canton || "—"}</td>
              <td style="text-align: right;">{formatInt(r.impressions)}</td>
              <td style="text-align: right;">{fmtMin(r.median_minutes)}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

function DistTable({
  title,
  data,
  keyHeader,
}: {
  title: string;
  data: Record<string, number> | undefined;
  keyHeader: string;
}) {
  const entries = Object.entries(data ?? {}).sort((a, b) => b[1] - a[1]);
  return (
    <div>
      {title && <h3 style="font-size: 13px; margin: 0 0 8px;">{title}</h3>}
      <table class="table">
        <thead>
          <tr>
            <th>{keyHeader}</th>
            <th style="text-align: right;">{t("common.count")}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={2} class="muted">
                {t("signals.noneInWindow")}
              </td>
            </tr>
          ) : (
            entries.map(([k, n]) => (
              <tr key={k}>
                <td>{k}</td>
                <td style="text-align: right;">{formatInt(n)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
